import { Controller } from 'react-hook-form'
import apiClient from '@/lib/api/apiClient'
import { showApiError } from '@/lib/utils/notifications'
import styles from '../../styles/emailTemplateCard.module.css'
import { CKEditor4 } from '@/components/editor/CKEditor4'
import { cn } from '@/lib/utils/helpers'
import { PRIMARY_BRAND } from '../../utils/constants'
import type { EmailTemplateAddEditFormProps } from '../../types/components'
import { EmailTemplateFieldError } from './EmailTemplateFieldError'
import { EmailTemplateFormSection } from './EmailTemplateFormSection'
import { EmailTemplateVariablesList } from './EmailTemplateVariablesList'

export function EmailTemplateContentSection({ page }: EmailTemplateAddEditFormProps) {
  const {
    availableVars,
    control,
    registerField,
    watch,
    errors,
    showFieldError,
    fieldErrorMessage,
    clearErrors,
    validateFieldOnBlur,
    isPreview,
    setIsPreview,
    workflowType: wt,
    cbConfig,
  } = page

  const handleEditorPaste = async (evt: any, instance: any) => {
    const file =
      evt.data?.dataTransfer?.getFile?.(0) ||
      evt.data?.dataTransfer?.$?.files?.[0] ||
      evt.data?.dataValue?.files?.[0]
    if (file && file.type?.startsWith('image/')) {
      evt.cancel?.()
      try {
        const formData = new FormData()
        formData.append('emailcontent', file)
        const res = await apiClient.post<string>('/api/emailcontent_file_upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        const imageUrl = typeof res.data === 'string' ? res.data : (res.data as any)?.url
        if (imageUrl && instance?.insertHtml) {
          instance.insertHtml(`<img src="${imageUrl}" width="150px"></img>`)
        }
      } catch (e) {
        showApiError(e)
      }
    }
  }

  return (
    <EmailTemplateFormSection testId="et-section-email-content" title="Email Template">
      <p className={cn(styles.privacyNote, 'mt-0 mb-3 text-sm text-slate-700')}>
        Subject<span className={styles.requiredStar}>*</span>
        <br />
        You can add any variable from this email template to the &quot;Subject&quot; line. Simply type a $
        followed by the name of the attribute.
        <br />
        <strong>For example: $propertyName</strong>
      </p>

      <div className={styles.formRow}>
        <div className={cn(styles.formCol, styles.formColHalf, 'min-w-0')}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel} htmlFor="et-subject">
              Subject <span className={styles.requiredStar}>*</span>
            </label>
            <input
              id="et-subject"
              className={cn(
                styles.customFormControl,
                'min-h-[2.375rem] w-full',
                errors.email_subject && '!border-red-500',
              )}
              placeholder="Subject *"
              {...registerField('email_subject')}
            />
            <EmailTemplateFieldError
              show={showFieldError('email_subject')}
              message={fieldErrorMessage('email_subject')}
            />
          </div>
        </div>
      </div>

      {/* Angular: Available Variables alert sits after Subject, before Template Body */}
      <EmailTemplateVariablesList
        variables={availableVars}
        className="mb-4"
        testId="et-available-variables"
        footer={
          wt === 'DENIED_APPLICATION' && cbConfig?.active != null ? (
            <div className="mt-3">
              <h4 className="font-semibold text-slate-900">Credit Report Type:</h4>
              <p className="mt-1 m-0 text-sm text-slate-800">{String(cbConfig.active)}</p>
            </div>
          ) : null
        }
      />

      <div className="mb-1 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setIsPreview(false)}
          className={cn(
            'rounded-md px-3 py-1.5 text-sm font-medium',
            !isPreview ? 'text-white' : 'text-slate-600 bg-slate-100 hover:bg-slate-200',
          )}
          style={!isPreview ? { background: PRIMARY_BRAND } : undefined}
        >
          Template body *
        </button>
        <button
          type="button"
          onClick={() => setIsPreview(true)}
          className={cn(
            'rounded-md px-3 py-1.5 text-sm font-medium',
            isPreview ? 'text-white' : 'text-slate-600 bg-slate-100 hover:bg-slate-200',
          )}
          style={isPreview ? { background: PRIMARY_BRAND } : undefined}
        >
          Preview
        </button>
      </div>

      <EmailTemplateFieldError
        show={showFieldError('email_body')}
        message={fieldErrorMessage('email_body') ?? errors.email_body?.message}
        className="mb-2"
      />

      {!isPreview && (
        <div className="min-h-[240px] w-full min-w-0">
          <Controller
            name="email_body"
            control={control}
            render={({ field }) => (
              <CKEditor4
                id="et-email_body"
                value={field.value || ''}
                onChange={(html) => {
                  field.onChange(html)
                  clearErrors('email_body')
                }}
                onBlur={() => {
                  field.onBlur()
                  validateFieldOnBlur('email_body')
                }}
                onPaste={handleEditorPaste}
                className="max-w-full"
              />
            )}
          />
        </div>
      )}

      {isPreview && (
        <div className="min-h-[240px] w-full min-w-0 overflow-x-auto rounded border border-slate-200/80">
          <iframe
            title="email-body-preview"
            className="h-[320px] w-full min-w-0 bg-white"
            srcDoc={watch('email_body') || '<!DOCTYPE html><html><body></body></html>'}
            sandbox="allow-same-origin"
          />
        </div>
      )}
    </EmailTemplateFormSection>
  )
}
